import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'

// Components
import Text from './Text'
import PeopleRow, { Container, ICta } from './feedback/PeopleRow'
import GoalCard, { getGoalType } from './card/GoalCard'
import { JournalMessage } from './card/Journal'
import { RoundButton } from './pageTypes/TermEvaluationsProgress'
import { Icon } from './icon'
import EvaluationHeader from './EvaluationHeader'
import StickyHeaderLayoverModal, {
  IOption,
} from './feedback/StickyHeaderLayoverModal'
import ResultsHeader from './ResultsHeader'
import ValueCard from './card/ValueCard'
import CompetencyCard from './card/CompetencyCard'
import Fold from './Fold'
import MemberStatBlocks, { IStatAssessor } from './evaluations/MemberStatBlocks'
import EvaluationOverviewList, {
  IOverviewRow,
  OverviewMode,
} from './evaluations/EvaluationOverviewList'

// Styles
import { mqFrom } from '../styles/mediaQueries'

// Utils
import { useAppDispatch, useAppState } from '../context/AppContext'
import { calculateDaysLeft, formatDate } from '../utils/dateHelper'
import { cultureValueColors } from '../utils/habitHelper'
import { createColor } from '../styles/colors'
import {
  calculateAverageExpectationValues,
  calculatePercentageAlignmentValue,
  combineAssessments,
  findOverlappingEntities,
  mergeUniqueAssessors,
  areAllScoresByContributorsSubmitted,
  getClosestStep,
  getSkillScores,
  getCultureScores,
  getAvgGoalEvaluations,
} from '../utils/evaluationHelpers'
import { notEmpty } from '../utils/typeGuards'
import { getMemberStatData } from '../utils/memberStatData'
import { memberHasNoSkills } from '../common/helpers/member.helper'
import { loadPersonalPdpService } from '../context/services/pdp/loadPersonalPdp.service'
import { getAllowedContributors } from '../common/helpers/company.helper'
import { adaptBackendCompanyToFrontendCompany } from '../context/services/loadCompany.service'

// Types
import {
  ICompanyMember,
  ICompany,
  ICompanyPure,
} from '../common/models/Company'
import {
  EvaluationAssessmentType,
  IEvaluationAssessor,
  IEvaluationPure,
  IEvaluationState,
  ISingleEvaluationPure,
  SingleEvaluationStatus,
  SUBMITTED_KEY,
} from '../common/models/Evaluation'
import {
  GoalChatMessageType,
  GoalType,
  IPdpPure,
  PdpStatus,
} from '../common/models/Pdp'
import { CompetencyType } from '../common/models/Feedback'
import { AnyColor } from '../common/models/colors'

export type EvaluationModalType =
  | 'skillResults'
  | 'goalsResults'
  | 'culturesResults'
  | 'addAssessors'
  | 'viewAssessors'
  | 'plannedEvaluation'

interface IProps {
  initialType: EvaluationModalType
  member: ICompanyMember
  evaluations: IEvaluationState[] | IEvaluationPure[]
  evalCompany?: ICompany | ICompanyPure
  pdp: IPdpPure
  isDeleting?: boolean
  isOwnProfile: boolean
  closeModal: () => void
  onBack?: () => void
  defaultFilter?: string
  addAssessor?: (
    value: React.SetStateAction<
      | {
          member: ICompanyMember
          type: EvaluationAssessmentType
          assessors?: IEvaluationAssessor[]
        }
      | undefined
    >
  ) => void
  removeAssessor?: (
    evaluation: IEvaluationState | IEvaluationPure,
    assessorId: string[],
    typeOfAssessor: EvaluationAssessmentType
  ) => Promise<void>
  reopenAssessment?: (
    evaluation: IEvaluationState | IEvaluationPure,
    assessorId: string,
    typeOfAssessor: EvaluationAssessmentType
  ) => Promise<void>
  // Hide the top summary MemberStatBlocks (kept for the manage/admin mount,
  // suppressed when the modal is opened from the collection member page).
  hideSummaryBlocks?: boolean
  // Previous-term evaluation + the company version it was created under, used
  // to compute the "vs last" deltas in the member stat blocks. Only supplied
  // when a compare period is active.
  previousEvaluation?: IEvaluationState | IEvaluationPure
  compareCompanyVersion?: ICompany | ICompanyPure
  // Seed props used when this modal is mounted as a nested results layer on top
  // of an overview instance (the drill-down stack). All optional so existing
  // call sites are unaffected.
  initialEntityId?: string // preselect this entity in the results dropdown (row drill)
  initialSelectedMember?: ICompanyMember // preselect assessor (CTA "show" drill)
  initialFilteredAssessments?: AssessmentWithMemberId[] // single-assessment preset (CTA)
}

interface AssessmentWithMemberId extends ISingleEvaluationPure {
  memberId: string
}

type EvaluationReduceType = {
  assessment: AssessmentWithMemberId[]
  assessors: IEvaluationAssessor[]
  termNumber?: number
}

// Map between an assessment type and its matching Results view type.
const resultTypeForAssessment = (
  evalType: EvaluationAssessmentType
): EvaluationModalType =>
  evalType === 'culture'
    ? 'culturesResults'
    : evalType === 'goals'
    ? 'goalsResults'
    : 'skillResults'

const assessmentTypeForResult = (
  resultType: EvaluationModalType
): EvaluationAssessmentType =>
  resultType === 'culturesResults'
    ? 'culture'
    : resultType === 'goalsResults'
    ? 'goals'
    : 'skills'

const EvaluationModal: React.FC<IProps> = ({
  initialType,
  member,
  evaluations,
  pdp: initialPdp,
  defaultFilter,
  isOwnProfile,
  onBack,
  closeModal,
  addAssessor,
  removeAssessor,
  reopenAssessment,
  hideSummaryBlocks,
  previousEvaluation,
  compareCompanyVersion,
  evalCompany: eCompany,
  initialEntityId,
  initialSelectedMember,
  initialFilteredAssessments,
}) => {
  const history = useHistory()
  const { theme, company, userAuthToken, user } = useAppState()
  const appDispatch = useAppDispatch()
  const { t } = useTranslation()
  // Fixed for this instance's lifetime: the base stays on its overview type and
  // a nested instance stays on its results type (drilling mounts a new layer).
  const [type] = useState<EvaluationModalType>(initialType)
  const [dropdowns, setDropdowns] = useState<
    Array<{
      type: EvaluationModalType
      options: Array<IOption<AssessmentWithMemberId[]>>
    }>
  >()
  const [filteredAssessments, setFilteredAssessments] = useState<
    AssessmentWithMemberId[]
  >(initialFilteredAssessments ?? [])

  const evalCompany =
    eCompany && !('allMembers' in eCompany)
      ? adaptBackendCompanyToFrontendCompany(eCompany)
      : eCompany

  const [evaluationState, setEvaluationState] = useState<EvaluationReduceType>()
  const [pdp] = useState(initialPdp)
  // Only set when opened as a nested results layer (seeded from the drill); used
  // to scope results to one assessor and for the header avatar.
  const [selectedMember] = useState<ICompanyMember | undefined>(
    initialSelectedMember
  )
  // Per-section Assessors|Results toggle in the overview (viewAssessors) view.
  const [overviewMode, setOverviewMode] = useState<
    Record<EvaluationAssessmentType, OverviewMode>
  >({ goals: 'assessors', skills: 'assessors', culture: 'assessors' })
  // A result-row drill: preselect the clicked entity in its dropdown once that
  // dropdown has been built. Seeded from initialEntityId when this modal opens
  // as a nested results layer.
  const [pendingDrill, setPendingDrill] = useState<
    | {
        evalType: EvaluationAssessmentType
        entityId: string
      }
    | undefined
  >(
    initialEntityId
      ? {
          evalType: assessmentTypeForResult(initialType),
          entityId: initialEntityId,
        }
      : undefined
  )
  // When set, a nested EvaluationModal is mounted on top as a stacked results
  // layer; the overview instance itself stays on its overview type.
  const [drill, setDrill] = useState<{
    resultType: EvaluationModalType
    entityId?: string
    selectedMember?: ICompanyMember
    presetAssessments?: AssessmentWithMemberId[]
    pdp?: IPdpPure
  }>()

  const membersMap =
    company && evaluationState
      ? evaluationState.assessors.reduce((acc, assessor) => {
          const foundMember = company.allMembers.find(
            (mem) => mem._id === assessor.memberId
          )

          if (foundMember) {
            acc.set(assessor.memberId, foundMember)
          }

          return acc
        }, new Map<string, ICompanyMember>())
      : new Map()

  if (
    evalCompany &&
    (!evalCompany.settings || !evalCompany.settings.evaluations)
  ) {
    evalCompany.settings = company?.settings
  }

  const allowedAssessments =
    evaluationState &&
    evalCompany &&
    evaluationState.assessment.filter((a) =>
      getAllowedContributors(evalCompany, member).some(
        (s) => s._id === a.senderId
      )
    )

  const skillsMap =
    allowedAssessments &&
    calculateAverageExpectationValues(
      allowedAssessments.filter((a) => a.skillsSubmitted)
    )

  const contributorsDone =
    evalCompany &&
    evaluationState &&
    areAllScoresByContributorsSubmitted(
      evaluationState.assessment,
      evaluationState.assessors,
      getAllowedContributors(evalCompany, member).map((m) => m._id)
    )

  const cultureAlignmentValueMap =
    allowedAssessments &&
    calculatePercentageAlignmentValue(
      allowedAssessments.filter((a) => a.cultureSubmitted)
    )

  const modalConfig = {
    skillResults: {
      title: 'evaluationModal.titles.skillEvaluationResults',
      subtitle: 'evaluationModal.subtitles.skillEvaluationResults',
      dropdowns: [{ filter: 'assessors' }, { filter: 'skills' }],
    },
    goalsResults: {
      title: 'evaluationModal.titles.termEvaluationResults',
      subtitle: 'evaluationModal.subtitles.termEvaluationResults',
      dropdowns: [{ filter: 'assessors' }, { filter: 'goals' }],
    },
    culturesResults: {
      title: 'evaluationModal.titles.cultureEvaluationResults',
      subtitle: 'evaluationModal.subtitles.cultureEvaluationResults',
      dropdowns: [{ filter: 'assessors' }, { filter: 'culture' }],
    },
    viewAssessors: {
      title: 'evaluationModal.titles.evaluationAssessorDetails',
      subtitle: 'evaluationModal.subtitles.termOverviewWithEvaluations',
      dropdowns: [{ filter: 'type' }],
    },
    addAssessors: {
      title: 'evaluationModal.titles.evaluationAssessorDetails',
      subtitle: 'evaluationModal.subtitles.termOverviewWithAssessors',
      dropdowns: [{ filter: 'type' }],
    },
    plannedEvaluation: {
      title: 'evaluationModal.titles.plannedEvaluation',
      subtitle: 'evaluationModal.subtitles.plannedEvaluation',
      dropdowns: [{ filter: 'type' }],
    },

    default: {
      title: 'evaluationModal.titles.evaluationAssessorDetails',
      subtitle: 'evaluationModal.subtitles.termOverviewWithAssessors',
      dropdowns: [{ filter: 'type' }],
    },
  }

  const modalDetails = modalConfig[type] || modalConfig.default

  const populateFilters = () => {
    const {
      combinedGoals,
      combinedSkills,
      combinedCultures,
    } = combineAssessments(evaluationState!.assessment, true, true, true)

    setDropdowns(
      modalDetails.dropdowns.map((dropdown) => {
        if (dropdown.filter === 'type') {
          return {
            type,
            options: [
              {
                value: 'all',
                label: t('general.all'),
                selected: defaultFilter ? false : true,
                results: evaluationState!.assessment,
              },
              {
                value: 'skills',
                label: t('general.skills'),
                selected:
                  (defaultFilter && defaultFilter === 'skills') || false,
                disabled:
                  !evaluationState!.assessors.some(
                    (assessor) => assessor.skills
                  ) ||
                  // From the collection hub: also disable when the section has no
                  // visible results to show (hidden by reveal / nothing submitted).
                  (hideSummaryBlocks &&
                    !evaluationState!.assessment.some(
                      (a) => a.skills && a.skills.length > 0
                    )),
                results: evaluationState!.assessment.map((assessment) => {
                  return { ...assessment, goals: [], culture: [] }
                }),
              },
              {
                value: 'culture',
                label: t('general.feedback.culture'),
                selected:
                  (defaultFilter && defaultFilter === 'culture') || false,
                disabled:
                  !evaluationState!.assessors.some(
                    (assessor) => assessor.culture
                  ) ||
                  (hideSummaryBlocks &&
                    !evaluationState!.assessment.some(
                      (a) => a.culture && a.culture.length > 0
                    )),
                results: evaluationState!.assessment.map((assessment) => {
                  return { ...assessment, skills: [], goals: [] }
                }),
              },
              {
                value: 'goals',
                label: t('general.goals.goals'),
                selected: (defaultFilter && defaultFilter === 'goals') || false,
                disabled:
                  !evaluationState!.assessors.some(
                    (assessor) => assessor.goals
                  ) ||
                  (hideSummaryBlocks &&
                    !evaluationState!.assessment.some(
                      (a) => a.goals && a.goals.length > 0
                    )),
                results: evaluationState!.assessment.map((assessment) => {
                  return { ...assessment, skills: [], culture: [] }
                }),
              },
            ],
          }
        } else if (dropdown.filter === 'goals') {
          return {
            type,
            options: [
              {
                value: 'all-goals',
                label: t('general.goals.allGoals'),
                selected: true,
                results: evaluationState!.assessment.filter(
                  (as) => as.goalsSubmitted
                ),
              },
              ...combinedGoals.map((goal) => {
                const pdpGoal = pdp.goals.find((g) => g._id === goal.goalId)

                return {
                  value: goal.goalId,
                  label:
                    pdpGoal?.submissions[pdpGoal.submissions.length - 1].title,
                  selected: false,
                  results: evaluationState!.assessment
                    .filter((as) => as.goalsSubmitted)
                    .map((assessment) => ({
                      ...assessment,
                      goals: assessment.goals.filter(
                        (g) => g.goalId === goal.goalId
                      ),
                    })),
                }
              }),
            ],
          }
        } else if (dropdown.filter === 'skills') {
          return {
            type,
            options: [
              {
                value: 'all-skills',
                label: t('general.allSkills'),
                selected: true,
                results: evaluationState!.assessment.filter(
                  (as) => as.skillsSubmitted
                ),
              },
              ...combinedSkills.map((skill) => {
                const goalType = getGoalType(
                  evalCompany || company!,
                  theme,
                  undefined,
                  skill.skillId
                )

                return {
                  value: skill.skillId,
                  label: goalType.title,
                  selected: false,
                  results: evaluationState!.assessment
                    .filter((as) => as.skillsSubmitted)
                    .map((assessment) => ({
                      ...assessment,
                      skills: assessment.skills.filter(
                        (s) => s.skillId === skill.skillId
                      ),
                    })),
                }
              }),
            ],
          }
        } else if (dropdown.filter === 'culture') {
          return {
            type,
            options: [
              {
                value: 'all-cultures',
                label: t('userProfileCulture.allValues'),
                selected: true,
                results: evaluationState!.assessment.filter(
                  (as) => as.cultureSubmitted
                ),
              },
              ...combinedCultures.map((culture) => {
                const goalType = getGoalType(
                  evalCompany || company!,
                  theme,
                  culture.habitId
                )

                return {
                  value: culture.habitId,
                  label: goalType.title,
                  selected: false,
                  results: evaluationState!.assessment
                    .filter((as) => as.cultureSubmitted)
                    .map((assessment) => ({
                      ...assessment,
                      culture: assessment.culture.filter(
                        (c) => c.habitId === culture.habitId
                      ),
                    })),
                }
              }),
            ],
          }
        } else if (dropdown.filter === 'assessors') {
          const selectedAssessor =
            filteredAssessments.length === 1
              ? filteredAssessments[0]
              : undefined

          return {
            type,
            options: [
              ...(initialType !== 'plannedEvaluation'
                ? [
                    {
                      value: 'all-assessors',
                      label: t('evaluationModal.allAssessors'),
                      selected: false,
                      results: evaluationState!.assessment.filter(
                        (as) =>
                          as.status !==
                            SingleEvaluationStatus.NOT_COMPLETE_IN_TIME &&
                          as[
                            type === 'culturesResults'
                              ? 'cultureSubmitted'
                              : type === 'goalsResults'
                              ? 'goalsSubmitted'
                              : 'skillsSubmitted'
                          ]
                      ),
                    },
                  ]
                : []),
              ...(evaluationState!.assessors
                ? evaluationState!.assessors
                    .map((assessor) => {
                      if (type === 'culturesResults' && !assessor.culture) {
                        return
                      }

                      if (type === 'goalsResults' && !assessor.goals) {
                        return
                      }

                      if (type === 'skillResults' && !assessor.skills) {
                        return
                      }

                      const assessmentOfAssessor = evaluationState!.assessment.find(
                        (assessment) =>
                          assessment.senderId === assessor.memberId &&
                          assessment[
                            type === 'culturesResults'
                              ? 'cultureSubmitted'
                              : type === 'goalsResults'
                              ? 'goalsSubmitted'
                              : 'skillsSubmitted'
                          ] && // only show assessors that have submitted for this type
                          assessment.status !==
                            SingleEvaluationStatus.NOT_COMPLETE_IN_TIME
                      )
                      if (!assessmentOfAssessor) {
                        return
                      }
                      return {
                        value: assessor.memberId,
                        label: membersMap.get(assessor.memberId)?.name || '',
                        selected:
                          assessor.memberId === selectedAssessor?.senderId,
                        results: [assessmentOfAssessor],
                      }
                    })
                    .filter(notEmpty)
                : []),
            ],
          }
        }

        return {
          type,
          options: [
            {
              value: t('general.all'),
              label: 'all',
              selected: true,
              results: evaluationState!.assessment,
            },
          ],
        }
      })
    )
  }

  const showAssessment = (typeOfAssessor: EvaluationAssessmentType) => {
    return evaluationState?.assessors.find(
      (assessor) =>
        assessor[typeOfAssessor] &&
        (type === 'plannedEvaluation'
          ? evaluationState.assessment.some(
              (assessment) => assessment.senderId === assessor.memberId
            )
          : true)
    )
  }

  const isAssessmentDoneForType = (
    assessment: AssessmentWithMemberId,
    evalType: EvaluationAssessmentType,
    evaluation?: IEvaluationPure
  ) => {
    const assessor = evaluation?.assessors.find(
      (as) => as.memberId === assessment.senderId
    )
    return (
      assessment?.status === SingleEvaluationStatus.DONE ||
      (evalType === 'goals' && assessment.goalsSubmitted && assessor?.goals) ||
      (evalType === 'skills' &&
        assessment.skillsSubmitted &&
        assessor?.skills) ||
      (evalType === 'culture' &&
        assessment.cultureSubmitted &&
        assessor?.culture)
    )
  }

  const getCTA = (
    assessment: AssessmentWithMemberId,
    evalType: EvaluationAssessmentType,
    evaluation?: IEvaluationPure
  ): ICta | undefined => {
    const assessor = evaluation?.assessors.find(
      (as) => as.memberId === assessment.senderId
    )
    if (
      type === 'plannedEvaluation' &&
      assessment.status !== SingleEvaluationStatus.DONE &&
      assessment.status !== SingleEvaluationStatus.NOT_COMPLETE_IN_TIME &&
      (evalType === 'skills'
        ? !assessment.skillsSubmitted
        : evalType === 'culture'
        ? !assessment.cultureSubmitted
        : !assessment.goalsSubmitted)
    ) {
      const evaluationId = evaluation?._id

      return {
        onClick: () => {
          history.push(`/evaluation/${evaluationId}/${evalType}`)
        },
        type:
          assessment?.status === SingleEvaluationStatus.IN_PROGRESS
            ? 'continue'
            : 'start',
      }
    }

    if (isAssessmentDoneForType(assessment, evalType, evaluation)) {
      return {
        onClick: () => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          ;(async () => {
            const loadedPdp = await getPdpForDrill(
              evaluation!.memberId,
              evaluation!.termNumber,
              evaluation?._id
            )
            setDrill({
              resultType: resultTypeForAssessment(evalType),
              selectedMember: membersMap.get(assessment.memberId),
              presetAssessments: [assessment],
              pdp: loadedPdp,
            })
          })()
        },
        type: 'show',
      }
    }

    if (
      (assessment.status === SingleEvaluationStatus.NOT_STARTED ||
        assessment.status === SingleEvaluationStatus.IN_PROGRESS) &&
      removeAssessor &&
      evaluation
    ) {
      const assessorAsMember = membersMap.get(assessor?.memberId)
      return {
        onClick: () => {
          appDispatch({
            type: 'updateAlertPopup',
            payload: {
              title: t('evaluationModal.removeModal.title'),
              subtitle: t('general.areYouSure'),
              content: (
                <Text textAlign="center">
                  {t('evaluationModal.removeModal.content', {
                    firstNameOne: assessorAsMember.firstName,
                    firstNameTwo: member.firstName,
                  })}
                  {assessment.status === SingleEvaluationStatus.IN_PROGRESS && (
                    <>
                      <br />
                      {t('evaluationModal.removeModal.inProgressContent')}
                    </>
                  )}
                </Text>
              ),
              actionButton: t('evaluationModal.removeModal.buttonName'),
              dismissButton: t('general.actions.cancel'),
              onSubmit: async () => {
                await removeAssessor(
                  evaluation,
                  [assessment.senderId],
                  evalType
                )
                appDispatch({
                  type: 'updateAlertPopup',
                  payload: undefined,
                })
              },
            },
          })
        },
        type: 'remove',
      }
    }

    return undefined
  }

  const getSecondCta = (
    assessment: AssessmentWithMemberId,
    evalType: EvaluationAssessmentType,
    evaluation?: IEvaluationPure
  ): ICta | undefined => {
    if (
      !user?.asCompanyMember?.isAdmin ||
      !reopenAssessment ||
      !evaluation ||
      type === 'plannedEvaluation'
    ) {
      return undefined
    }
    // Reopening is disabled once the term's evaluation phase is closed
    const termClosed = company?.termsInformation?.terms?.find(
      (term) => term.termNumber === evaluation.termNumber
    )?.evaluationPhaseClosed
    if (termClosed) {
      return undefined
    }
    if (!isAssessmentDoneForType(assessment, evalType, evaluation)) {
      return undefined
    }

    const assessorAsMember = membersMap.get(assessment.senderId)

    return {
      type: 'refresh',
      onClick: () => {
        appDispatch({
          type: 'updateAlertPopup',
          payload: {
            title: t('evaluationModal.reopenModal.title'),
            subtitle: t('general.areYouSure'),
            content: (
              <Text textAlign="center">
                {t('evaluationModal.reopenModal.content', {
                  firstNameOne: assessorAsMember?.firstName,
                  firstNameTwo: member.firstName,
                })}
              </Text>
            ),
            actionButton: t('evaluationModal.reopenModal.buttonName'),
            dismissButton: t('general.actions.cancel'),
            onSubmit: async () => {
              await reopenAssessment(evaluation, assessment.senderId, evalType)
              appDispatch({
                type: 'updateAlertPopup',
                payload: undefined,
              })
            },
          },
        })
      },
    }
  }

  // Loads the subject's PDP for a drill (the drilled assessment can belong to a
  // different subject than the base member), returning it so it can be passed to
  // the nested results layer rather than mutating this instance's state.
  const getPdpForDrill = async (
    memberId: string,
    termNumber: number,
    evaluationId: string
  ): Promise<IPdpPure> => {
    if (
      evaluations.length === 0 ||
      !company ||
      !userAuthToken ||
      (user?.asCompanyMember?._id === memberId &&
        termNumber === pdp?.termNumber)
    ) {
      return pdp
    }

    const { savedPdp } = await loadPersonalPdpService(
      memberId,
      termNumber,
      userAuthToken,
      company,
      evaluationId
    )

    return savedPdp || pdp
  }

  const getNoAssessorMessage = (
    evalType: EvaluationAssessmentType,
    hasGoals: boolean
  ) => {
    const currentTerm =
      evaluationState &&
      company?.termsInformation?.terms &&
      company.termsInformation.terms.find(
        (term) => term.termNumber === evaluationState?.termNumber
      )

    const translationKey = (evalType === 'culture'
      ? t('general.feedback.culture')
      : t('general.skills')
    ).toLowerCase()

    if (evalType === 'goals') {
      if (type === 'plannedEvaluation') {
        return t('evaluationModal.noGoalsToEvaluate')
      }

      if (
        currentTerm?.evaluationPhaseOpened &&
        !currentTerm.evaluationPlanningComplete &&
        !hasGoals
      ) {
        return t('evaluationModal.noPublishedGoalsBeforeStartMessage', {
          firstName: member.firstName,
        })
      }

      if (!hasGoals && currentTerm?.evaluationPlanningComplete) {
        return t('evaluationModal.noPublishedGoalsAfterStartMessage', {
          firstName: member.firstName,
        })
      }

      if (currentTerm?.evaluationPhaseClosed) {
        return t('evaluationModal.publishedGoalsNoAssessorsFinalizeMessage', {
          firstName: member.firstName,
        })
      }

      return t('evaluationModal.publishedGoalsNoAssessorsMessage', {
        firstName: member.firstName,
      })
    } else if (evalType === 'skills') {
      if (type === 'plannedEvaluation') {
        return t('evaluationModal.noSkillsToEvaluate')
      }

      const noSkills =
        company && memberHasNoSkills(member, evalCompany || company)

      if (noSkills) {
        return t('evaluationModal.noSkillsMessage', {
          firstName: member.firstName,
        })
      }

      if (currentTerm?.evaluationPhaseClosed) {
        return t('evaluationModal.noAssessorsAfterFinalizeMessage', {
          firstName: member.firstName,
          evalType: translationKey,
        })
      }

      return t('evaluationModal.noAssessorsBeforeFinalizeMessage', {
        firstName: member.firstName,
        evalType: translationKey,
      })
    }

    if (type === 'plannedEvaluation') {
      return t('evaluationModal.noCulturesToEvaluate')
    }

    if (currentTerm?.evaluationPhaseClosed) {
      return t('evaluationModal.noAssessorsAfterFinalizeMessage', {
        firstName: member.firstName,
        evalType: translationKey,
      })
    }

    return t('evaluationModal.noAssessorsBeforeFinalizeMessage', {
      firstName: member.firstName,
      evalType: translationKey,
    })
  }

  useEffect(() => {
    setEvaluationState(
      (evaluations as IEvaluationState[]).reduce<EvaluationReduceType>(
        (result, evaluation) => ({
          // combine assessments and assessors
          assessment: [
            ...result.assessment,
            ...evaluation.assessment.map((ass) => {
              return { ...ass, memberId: evaluation.memberId }
            }),
          ],
          assessors: mergeUniqueAssessors([
            ...result.assessors,
            ...evaluation.assessors,
          ]),
          termNumber: evaluation.termNumber,
        }),
        {
          assessment: [],
          assessors: [],
          termNumber: undefined,
        }
      )
    )
  }, [evaluations])

  useEffect(() => {
    if (
      !evaluationState?.assessors ||
      !modalDetails.dropdowns ||
      // Don't reset dropdowns if already set and type didn't change
      (dropdowns !== undefined && dropdowns[0].type === type)
    ) {
      return
    }
    populateFilters()
  }, [evaluationState, type])

  // Default each overview section to Results when results are in, else
  // Assessors (the planning state, where Results stays gated/disabled).
  useEffect(() => {
    if (type !== 'viewAssessors' || !evaluationState) return
    const hasResults = (evalType: EvaluationAssessmentType) =>
      evaluationState.assessment.some((a) => a[SUBMITTED_KEY[evalType]])
    setOverviewMode({
      goals: hasResults('goals') ? 'results' : 'assessors',
      skills: hasResults('skills') ? 'results' : 'assessors',
      culture: hasResults('culture') ? 'results' : 'assessors',
    })
  }, [evaluationState, type])

  // Once the Results dropdowns have rebuilt for the drilled-to type, select the
  // clicked entity — the same path the header dropdown buttons use, so the
  // existing getAllResults effect recomputes filteredAssessments for it.
  useEffect(() => {
    if (!pendingDrill || !dropdowns) return
    const idx = dropdowns.findIndex((dr) =>
      dr.options.some((op) => op.value === pendingDrill.entityId)
    )
    if (idx === -1) return
    setDropdowns(
      dropdowns.map((dr, i) =>
        i === idx
          ? {
              ...dr,
              options: dr.options.map((op) => ({
                ...op,
                selected: op.value === pendingDrill.entityId,
              })),
            }
          : dr
      )
    )
    setPendingDrill(undefined)
  }, [pendingDrill, dropdowns])

  const onOverviewRowClick = (
    evalType: EvaluationAssessmentType,
    entityId: string
  ) => {
    // Open the matching Results view as a nested layer, preselecting the entity.
    setDrill({ resultType: resultTypeForAssessment(evalType), entityId })
  }

  // Stat-card drill-in: open the type's detail view as a nested layer with both
  // dropdowns left at their populate-default (all assessors + all entities).
  const onStatCardClick = (evalType: EvaluationAssessmentType) => {
    setDrill({ resultType: resultTypeForAssessment(evalType) })
  }

  useEffect(() => {
    if (
      dropdowns !== undefined &&
      dropdowns[0].options.find((op) => op.label === 'all')?.results.length !==
        evaluationState!.assessors.length
    ) {
      populateFilters()
    }
  }, [evaluationState?.assessors])

  useEffect(() => {
    if (dropdowns === undefined) return

    const getOverlappingGoalsAndSkill = () => {
      const selectedResults = dropdowns.flatMap((dropdown) =>
        dropdown.options
          .filter((option) => option.selected)
          .flatMap((option) => option.results)
      )

      const allGoalsByResult = selectedResults.map((result) => result.goals)
      const allSkillsByResult = selectedResults.map((result) => result.skills)
      const allCulturesByResult = selectedResults.map(
        (result) => result.culture
      )

      const overlappingGoals = findOverlappingEntities(
        allGoalsByResult,
        'goalId'
      )
      const overlappingSkills = findOverlappingEntities(
        allSkillsByResult,
        'skillId'
      )

      const overlappingCultures = findOverlappingEntities(
        allCulturesByResult,
        'habitId'
      )

      return {
        overlappingGoalIds: overlappingGoals.map((g) => g.goalId),
        overlappingSkillIds: overlappingSkills.map((s) => s.skillId),
        overlappingCultureIds: overlappingCultures.map((c) => c.habitId),
      }
    }

    const getAllResults = () => {
      let selectedAssessor: string | undefined
      const {
        overlappingGoalIds,
        overlappingSkillIds,
        overlappingCultureIds,
      } = getOverlappingGoalsAndSkill()

      const selectedResultsArrays = dropdowns
        .map((dropdown) => {
          const selectedOption = dropdown.options.find(
            (option) => option.selected
          )

          const assessor = evaluationState?.assessors.find(
            (a) => a.memberId === selectedOption?.value
          )

          if (assessor) {
            selectedAssessor = selectedOption?.value
            selectedOption &&
              initialType === 'plannedEvaluation' &&
              (selectedOption.results = selectedOption.results.filter(
                (r) => r.memberId === assessor?.memberId
              ))
          }

          return selectedOption?.results.map((result) => ({
            ...result,
            ...((selectedAssessor ||
              ['goalsResults', 'skillResults', 'culturesResults'].includes(
                type
              )) && {
              goals:
                overlappingGoalIds.length > 0
                  ? result.goals.filter((goal) =>
                      overlappingGoalIds.includes(goal.goalId)
                    )
                  : result.goals,
              skills:
                overlappingSkillIds.length > 0
                  ? result.skills.filter((skill) =>
                      overlappingSkillIds.includes(skill.skillId)
                    )
                  : result.skills,
              culture:
                overlappingCultureIds.length > 0
                  ? result.culture.filter((c) =>
                      overlappingCultureIds.includes(c.habitId)
                    )
                  : result.culture,
            }),
          }))
        })
        .filter((results): results is AssessmentWithMemberId[] =>
          Array.isArray(results)
        )

      if (selectedResultsArrays.length === 0) return []

      const combinedResults = selectedResultsArrays
        .flat()
        .reduce((combined, currentAssessment) => {
          if (!currentAssessment._id) return combined

          const isNewAssessment = !combined.some(
            (a) => a._id === currentAssessment._id
          )
          if (isNewAssessment) {
            combined.push(currentAssessment)
          }

          return selectedAssessor
            ? combined.filter((c) => c.senderId === selectedAssessor)
            : combined
        }, [] as AssessmentWithMemberId[])

      setFilteredAssessments(
        selectedMember
          ? combinedResults.filter(
              (result) => result.memberId === selectedMember._id
            )
          : combinedResults
      )
    }

    getAllResults()
  }, [dropdowns, evaluationState])

  const renderResults = (
    evalType: EvaluationAssessmentType,
    hideHeader?: boolean
  ) => {
    const hasGoals =
      pdp &&
      pdp.goals &&
      pdp.goals.length > 0 &&
      [PdpStatus.PUBLISHED, PdpStatus.IN_EVALUATION].includes(pdp.status)

    const showButton =
      evalType === 'culture' ||
      (evalType === 'goals'
        ? hasGoals
        : !memberHasNoSkills(member, evalCompany || company!))

    const selectedDropdown = dropdowns?.find((dr) =>
      dr.options.some((op) => op.value === evalType)
    )

    const selectedValue = selectedDropdown?.options.find((op) => op.selected)
      ?.value

    if (selectedValue !== 'all' && selectedValue !== evalType) {
      return
    }

    const filterByType =
      selectedValue === 'all' ||
      !(selectedValue && evalType && selectedValue !== evalType)

    const itemsLength = filterByType
      ? filteredAssessments.filter((assessment) => {
          const evaluation = (evaluations as IEvaluationPure[]).find((e) =>
            e.assessment.find((ass) => ass._id === assessment._id)
          )
          const assessor = evaluation?.assessors.find(
            (as) => as.memberId === assessment.senderId
          )

          return assessor && assessor[evalType]
        }).length
      : 0

    const translationKey = (evalType === 'culture'
      ? t('general.feedback.culture')
      : evalType === 'goals'
      ? t('general.goals.goals')
      : t('general.skills')
    ).toLowerCase()

    return (
      <>
        {!hideHeader && (
          <StyledResultsHeader
            title={
              evalType === 'skills'
                ? t('termEvaluationsProgress.skillAssessors')
                : evalType === 'goals'
                ? t('termEvaluationsProgress.goalAssessors')
                : t('termEvaluationsProgress.cultureAssessors')
            }
            resultsLength={itemsLength}
            icon={
              evalType === 'skills'
                ? 'Skills'
                : evalType === 'goals'
                ? 'Goals'
                : 'Culture'
            }
          />
        )}
        {showAssessment(evalType) ? (
          filterByType &&
          filteredAssessments.map((assessment, index) => {
            const evaluation = (evaluations as IEvaluationPure[]).find((e) =>
              e.assessment.find((ass) => ass._id === assessment._id)
            )
            const assessor = evaluation?.assessors.find(
              (as) => as.memberId === assessment.senderId
            )

            if (
              (evalType === 'skills' &&
                ((assessment.skills.length < 1 &&
                  assessor?.skills &&
                  assessment.skillsSubmitted) ||
                  !assessor?.skills)) ||
              (evalType === 'goals' &&
                ((assessment.goals.length < 1 &&
                  assessor?.goals &&
                  assessment.goalsSubmitted) ||
                  !assessor?.goals)) ||
              (evalType === 'culture' &&
                ((assessment.culture.length < 1 &&
                  assessor?.culture &&
                  assessment.cultureSubmitted) ||
                  !assessor?.culture))
            ) {
              return
            }

            const evaluatedMember = company?.allMembers.find(
              (m) => m._id === evaluation?.memberId
            )

            const assessorMember = membersMap.get(assessment.senderId)

            return (
              <StyledPeopleRow
                key={`people-row-${assessment.senderId}-${index}`}
                type={'incoming'}
                name={
                  type === 'plannedEvaluation'
                    ? evaluatedMember?.name
                    : assessorMember?.name
                }
                date={formatDate(
                  new Date(
                    assessment[`${evalType}Submitted`] ||
                      assessment.dateSubmitted
                  )
                )}
                status={
                  (evalType === 'goals' &&
                    assessment.goalsSubmitted &&
                    assessor?.goals) ||
                  (evalType === 'skills' &&
                    assessment.skillsSubmitted &&
                    assessor?.skills) ||
                  (evalType === 'culture' &&
                    assessment.cultureSubmitted &&
                    assessor?.culture)
                    ? SingleEvaluationStatus.DONE
                    : assessment.status
                }
                overdue={
                  assessment.status !== SingleEvaluationStatus.DONE &&
                  (evalType === 'skills'
                    ? !assessment.skillsSubmitted && assessor?.skills
                    : evalType === 'goals'
                    ? !assessment.goalsSubmitted && assessor?.goals
                    : !assessment.cultureSubmitted && assessor?.culture) &&
                  calculateDaysLeft(
                    new Date(assessment.dateSubmitted).getTime()
                  ) < -30
                }
                bigAvatars
                avatars={[
                  assessorMember,
                  type === 'plannedEvaluation' ? evaluatedMember : member,
                ]}
                secondCta={getSecondCta(assessment, evalType, evaluation)}
                cta={getCTA(assessment, evalType, evaluation)}
              />
            )
          })
        ) : (
          <Text
            marginLeft={16}
            textStyle="label"
            color="textLight"
            marginTop={8}
            marginBottom={8}
          >
            {getNoAssessorMessage(evalType, hasGoals)}
          </Text>
        )}
        {type === 'addAssessors' && addAssessor && showButton && (
          <AddAssessorRow
            clickable
            onClick={() =>
              addAssessor({
                member,
                type: evalType,
                assessors: evaluationState!.assessors.filter(
                  (assessor) => assessor[evalType]
                ),
              })
            }
          >
            <RoundButton round colorVariant="secondary" icon="Plus" />
            <Text color="primary" textStyle="labelHeavy">
              {t('evaluationModal.addAssessors', {
                evalType: translationKey,
              })}
            </Text>
            <Icon type="ChevronRight" color={theme.colors.primary} size={12} />
          </AddAssessorRow>
        )}
      </>
    )
  }

  const skillValues = (company && getSkillScores(company)) || []
  const cultureValues = (company && getCultureScores(company)) || []

  // ---- Member overview (viewAssessors) -------------------------------------
  // How many leading segments are filled for a given average — the existing
  // "fill whole blocks up to the closest step" model (kept, not changed here).
  const segmentsFor = (
    values: Array<{ value: number; label: string }>,
    avg?: number
  ) =>
    typeof avg === 'number' && values.length
      ? {
          total: values.length,
          complete: values.filter(
            (v) => v.value <= getClosestStep(values, avg).value
          ).length,
        }
      : undefined

  const goalStatusLabel = (status?: number) =>
    status === 3
      ? t('forms.goalsAchieved')
      : status === 5
      ? t('forms.goalsOther')
      : status === 1
      ? t('forms.goalsNotAchieved')
      : undefined

  // Previous-term maps for the "vs last" deltas.
  const prevCompany =
    compareCompanyVersion && !('allMembers' in compareCompanyVersion)
      ? adaptBackendCompanyToFrontendCompany(compareCompanyVersion)
      : compareCompanyVersion
  if (
    prevCompany &&
    (!prevCompany.settings || !prevCompany.settings.evaluations)
  ) {
    prevCompany.settings = company?.settings
  }
  const prevMember =
    prevCompany?.members.find((m) => m._id === member._id) || member
  const prevAllowed =
    previousEvaluation && prevCompany
      ? previousEvaluation.assessment.filter((a) =>
          getAllowedContributors(prevCompany, prevMember).some(
            (s) => s._id === a.senderId
          )
        )
      : undefined
  const prevSkillsMap = prevAllowed
    ? calculateAverageExpectationValues(
        prevAllowed.filter((a) => a.skillsSubmitted)
      )
    : undefined
  const prevCultureMap = prevAllowed
    ? calculatePercentageAlignmentValue(
        prevAllowed.filter((a) => a.cultureSubmitted)
      )
    : undefined
  // Only surface a previous-term value when that section actually had a
  // submitted evaluation last term — otherwise the delta pill would render a
  // misleading delta against an empty previous. Mirrors prevSubmitted in
  // getMemberStatData.
  const prevSkillsSubmitted =
    !!prevAllowed && prevAllowed.some((a) => a.skillsSubmitted)
  const prevCultureSubmitted =
    !!prevAllowed && prevAllowed.some((a) => a.cultureSubmitted)

  // Goals, like skills/culture, only count from allowed score contributors who
  // actually submitted — feed getAvgGoalEvaluations a shielded assessment list.
  const currentGoalEvals = evaluations[0]
    ? getAvgGoalEvaluations({
        ...evaluations[0],
        assessment: (allowedAssessments || []).filter((a) => a.goalsSubmitted),
      })
    : []

  // skillsMap / cultureAlignmentValueMap are `Map | false | undefined`; narrow
  // to `Map | undefined` so optional chaining type-checks.
  const skillScoreMap = skillsMap || undefined
  const cultureScoreMap = cultureAlignmentValueMap || undefined

  // Shared, pure stat-block math (also drives the collection member page). The
  // assessor avatars are resolved here against the live company, so we override
  // the helper's assessor lists with the modal's own membersMap-based ones.
  const memberStatData =
    evaluationState &&
    getMemberStatData(
      evaluations[0] as IEvaluationPure | IEvaluationState | undefined,
      evaluationState,
      evalCompany,
      member,
      previousEvaluation,
      compareCompanyVersion
    )

  const assessorsFor = (evalType: EvaluationAssessmentType): IStatAssessor[] =>
    (evaluationState?.assessors || [])
      .filter((a) => a[evalType])
      .map((a) => {
        const m = membersMap.get(a.memberId)
        if (!m) return undefined
        const assessment = evaluationState?.assessment.find(
          (as) => as.senderId === a.memberId
        )
        return { member: m, submitted: !!assessment?.[SUBMITTED_KEY[evalType]] }
      })
      .filter(notEmpty)

  // A section only has a *score* when an allowed contributor submitted that
  // type — that's the same set the scores are computed from
  // (allowedAssessments). A submission by a non-contributor generates no score,
  // so it must not surface the stat blocks.
  const hasResultsFor = (evalType: EvaluationAssessmentType) =>
    !!allowedAssessments &&
    allowedAssessments.some((a) => a[SUBMITTED_KEY[evalType]])

  // Any submission of this type (incl. non-contributors) — the Results list
  // still renders those rows, so the Assessors|Results toggle must stay
  // available even when no contributor score was generated.
  const hasAnyResultsFor = (evalType: EvaluationAssessmentType) =>
    !!evaluationState?.assessment.some((a) => a[SUBMITTED_KEY[evalType]])

  const returnBody = () => {
    const {
      combinedGoals,
      combinedSkills,
      combinedCultures,
    } = combineAssessments(filteredAssessments, true, true, true)

    switch (type) {
      case 'goalsResults':
        return (
          <Body>
            {company &&
              combinedGoals.map((goal) => {
                const pdpGoal = pdp.goals.find((g) => g._id === goal.goalId)

                return (
                  <GoalCard
                    key={goal.goalId}
                    goalType={getGoalType(
                      evalCompany || company,
                      theme,
                      pdpGoal?.habitId,
                      pdpGoal?.skillId
                    )}
                    dateCreated={
                      pdpGoal?.dateCreated
                        ? new Date(pdpGoal?.dateCreated)
                        : new Date(goal.fields[0].dateSubmitted)
                    }
                    description={
                      pdpGoal?.submissions[pdpGoal.submissions.length - 1]
                        .description ?? ''
                    }
                    title={
                      pdpGoal?.submissions[pdpGoal.submissions.length - 1]
                        .title ?? ''
                    }
                    evaluationDone={contributorsDone?.goals}
                    journals={goal.fields.map((field) => {
                      return {
                        _id: goal.goalId,
                        dateCreated: new Date(field.dateSubmitted),
                        dateSubmitted: new Date(field.dateSubmitted),
                        senderId: field.senderId,
                        content: field.field.reasonText,
                        notificationId: 1,
                        messageType: GoalChatMessageType.SIMPLE_MESSAGE,
                        evaluationType: 'goal',
                        evaluationValue: field.field.goalStatus,
                      }
                    })}
                    member={member}
                    type={pdpGoal?.habitId ? GoalType.HABIT : GoalType.SKILL}
                    submissions={pdpGoal?.submissions ?? []}
                    isOwnProfile={false}
                    openJournals
                    withEvaluationResults
                  />
                )
              })}
          </Body>
        )
      case 'skillResults':
        return (
          <Body>
            {combinedSkills.map((skill, index) => {
              const map = new Map<number, ICompanyMember[]>()

              skill.fields.forEach(
                ({ field: { expectationValue }, senderId }) => {
                  const closestStep = getClosestStep(
                    skillValues,
                    expectationValue
                  ).value
                  if (!map.has(closestStep)) {
                    map.set(closestStep, [])
                  }
                  // membersMap can momentarily lag combinedSkills while a freshly
                  // mounted (nested) instance loads its evaluationState, and a
                  // sender may be an archived member missing from allMembers — skip
                  // those rather than pushing undefined (EvaluationHeader maps over it).
                  const sender = membersMap.get(senderId)
                  if (sender) map.get(closestStep)!.push(sender)
                }
              )

              const skillProps =
                company &&
                (evalCompany || company).skills.find(
                  (s) => s._id === skill.skillId
                )

              const skillAvg = skillsMap?.get(skill.skillId)

              return (
                company &&
                skillProps && (
                  <CompetencyCard
                    key={`skill-${skill.skillId}-${index}`}
                    competencyName={skillProps.name ?? ''}
                    competencyType={CompetencyType.skill}
                    description={skillProps.description}
                    levels={
                      member.jobLevel?.skillsIds.find(
                        (id) => skill.skillId === id
                      )
                        ? [member.jobLevel]
                        : []
                    }
                    descriptionFoldable
                  >
                    <Fold
                      caption={t('userProfileSkills.evaluationResults')}
                      opened
                      content={
                        <CardBody color="primaryLight">
                          <EvaluationHeader
                            key={`evaluation.header._id-${skill.skillId}-${index}`}
                            skillAverage={
                              typeof skillAvg === 'number'
                                ? Math.round(skillAvg)
                                : undefined
                            }
                            done={contributorsDone?.skills}
                            membersMap={map}
                            isOwnProfile={isOwnProfile}
                            variant="skills"
                            evalCompany={evalCompany}
                          />
                          {skill.fields.map((field, i) => (
                            <JournalMessage
                              key={`field._id-${skill.skillId}-${i}-${field.senderId}`}
                              message={{
                                _id: skill.skillId,
                                dateCreated: new Date(field.dateSubmitted),
                                dateSubmitted: new Date(field.dateSubmitted),
                                senderId: field.senderId,
                                content: field.field.reasonText,
                                messageType: 'text',
                              }}
                              variant="goal"
                            />
                          ))}
                        </CardBody>
                      }
                    />
                  </CompetencyCard>
                )
              )
            })}
          </Body>
        )
      case 'culturesResults':
        return (
          <Body>
            {combinedCultures.map((culture, index) => {
              const map = new Map<number, ICompanyMember[]>()

              culture.fields.forEach(
                ({ field: { alignmentValue }, senderId }) => {
                  const closestStep = getClosestStep(
                    cultureValues,
                    alignmentValue
                  ).value
                  if (!map.has(closestStep)) {
                    map.set(closestStep, [])
                  }
                  // membersMap can momentarily lag combinedSkills while a freshly
                  // mounted (nested) instance loads its evaluationState, and a
                  // sender may be an archived member missing from allMembers — skip
                  // those rather than pushing undefined (EvaluationHeader maps over it).
                  const sender = membersMap.get(senderId)
                  if (sender) map.get(closestStep)!.push(sender)
                }
              )

              const habit =
                company &&
                (evalCompany || company).allCultureValues.find(
                  (h) => h._id === culture.habitId
                )

              const cultureAvg = cultureAlignmentValueMap?.get(culture.habitId)

              return (
                habit && (
                  <ValueCard
                    key={`evaluation.value-card._id-${culture.habitId}-${index}`}
                    memberLevel={
                      selectedMember?.jobLevel ||
                      user?.asCompanyMember?.jobLevel
                    }
                    value={habit}
                    descriptionFoldable
                    resultTab={
                      <CardBody color="lightest">
                        <EvaluationHeader
                          key={`evaluation.header._id-${culture.habitId}-${index}`}
                          membersMap={map}
                          done={contributorsDone?.culture}
                          isOwnProfile={isOwnProfile}
                          habitColor={habit.color}
                          evalCompany={evalCompany}
                          culturePercentage={
                            typeof cultureAvg === 'number'
                              ? Math.round(cultureAvg)
                              : undefined
                          }
                          variant="culture"
                        />
                        {culture.fields.map((field, i) => {
                          if (field.field.textFeedback.length === 0) {
                            return
                          }

                          return (
                            <JournalMessage
                              key={`field._id-${culture.habitId}-${i}-${field.senderId}`}
                              message={{
                                _id: culture.habitId,
                                dateCreated: new Date(field.dateSubmitted),
                                dateSubmitted: new Date(field.dateSubmitted),
                                senderId: field.senderId,
                                content: field.field.textFeedback,
                                messageType: 'text',
                              }}
                              variant="goal"
                            />
                          )
                        })}
                      </CardBody>
                    }
                  />
                )
              )
            })}
          </Body>
        )
      default: {
        const allCombined = evaluationState
          ? combineAssessments(evaluationState.assessment, true, true, true)
          : { combinedGoals: [], combinedSkills: [], combinedCultures: [] }

        const goalStatusMap = new Map(
          currentGoalEvals.map((g) => [g.goalId, g.goalStatus])
        )

        const skillRows: IOverviewRow[] = allCombined.combinedSkills.map(
          (skill) => {
            const skillCompany = evalCompany || company!
            const skillDef = skillCompany.skills
              .concat(skillCompany.teamLeadSkills ?? [])
              .find((s) => s._id === skill.skillId)
            const current = skillScoreMap?.get(skill.skillId)
            const scored = typeof current === 'number'
            return {
              id: skill.skillId,
              title: skillDef?.name ?? '',
              category: skillDef?.tag || undefined,
              // Always render a bar: scored skills fill segments, unscored ones
              // show an empty track in a neutral grey (no primary colour).
              segments: scored
                ? segmentsFor(skillValues, current)
                : { total: skillValues.length, complete: 0 },
              trackColor: scored ? undefined : theme.colors.textLightest,
              current,
              previous:
                prevSkillsSubmitted && (prevSkillsMap?.size ?? 0) > 0
                  ? prevSkillsMap?.get(skill.skillId)
                  : undefined,
            }
          }
        )

        const cultureRows: IOverviewRow[] = allCombined.combinedCultures.map(
          (culture) => {
            const valueCompany = evalCompany || company!
            const habit = valueCompany.allCultureValues.find(
              (h) => h._id === culture.habitId
            )
            const hslArr = habit && valueCompany.companyColors?.[habit.color]
            const { darkValueColor, lightValueColor } = cultureValueColors(
              theme,
              habit?.color ?? '',
              valueCompany
            )
            const current = cultureScoreMap?.get(culture.habitId)
            const scored = typeof current === 'number'
            return {
              id: culture.habitId,
              title: habit?.name ?? '',
              category: habit?.subtitle,
              categoryColor: darkValueColor,
              round: true,
              icon: 'Culture',
              iconColor: darkValueColor,
              iconBg: lightValueColor,
              barColor: scored && hslArr ? createColor(hslArr, 50) : undefined,
              // Scored values colour their track; unscored ones fall back to a
              // neutral grey so the empty bar carries no culture colour.
              trackColor: scored ? lightValueColor : theme.colors.textLightest,
              segments: scored
                ? segmentsFor(cultureValues, current)
                : { total: cultureValues.length, complete: 0 },
              current,
              previous:
                prevCultureSubmitted && (prevCultureMap?.size ?? 0) > 0
                  ? prevCultureMap?.get(culture.habitId)
                  : undefined,
            }
          }
        )

        const goalRows: IOverviewRow[] = allCombined.combinedGoals.map(
          (goal) => {
            const pdpGoal = pdp.goals.find((g) => g._id === goal.goalId)
            return {
              id: goal.goalId,
              title:
                pdpGoal?.submissions[pdpGoal.submissions.length - 1]?.title ??
                '',
              statusLabel: goalStatusLabel(goalStatusMap.get(goal.goalId)),
            }
          }
        )

        const sectionFilter =
          dropdowns
            ?.find((dr) => dr.options.some((op) => op.value === 'all'))
            ?.options.find((op) => op.selected)?.value || 'all'
        const showSection = (evalType: EvaluationAssessmentType) =>
          sectionFilter === 'all' || sectionFilter === evalType

        const showStatBlocks =
          type === 'viewAssessors' &&
          !hideSummaryBlocks &&
          (sectionFilter === 'all'
            ? hasResultsFor('skills') ||
              hasResultsFor('culture') ||
              hasResultsFor('goals')
            : hasResultsFor(sectionFilter as EvaluationAssessmentType))

        return (
          <Body $plain>
            {showStatBlocks && memberStatData && (
              <MemberStatBlocks
                compareActive={memberStatData.compareActive}
                onCardClick={onStatCardClick}
                sectionFilter={
                  sectionFilter as 'all' | EvaluationAssessmentType
                }
                skill={{
                  ...memberStatData.skill,
                  assessors: assessorsFor('skills'),
                }}
                culture={{
                  ...memberStatData.culture,
                  assessors: assessorsFor('culture'),
                }}
                goals={{
                  ...memberStatData.goals,
                  subtitle: t('evaluationModal.goalsAchieved', {
                    achieved: memberStatData.goalsAchieved,
                    total: memberStatData.goalsTotal,
                  }),
                  assessors: assessorsFor('goals'),
                }}
              />
            )}
            {showSection('skills') && (
              <EvaluationOverviewList
                title={
                  t('general.skills') + ' ' + t('evaluationModal.overview')
                }
                icon="Skills"
                entityLabelKey="general.skill"
                resultsCount={skillRows.length}
                mode={overviewMode.skills}
                onModeChange={(m) =>
                  setOverviewMode((prev) => ({ ...prev, skills: m }))
                }
                resultsDisabled={!hasAnyResultsFor('skills')}
                rows={skillRows}
                assessorsContent={renderResults('skills', true)}
                onRowClick={(id) => onOverviewRowClick('skills', id)}
              />
            )}
            {showSection('culture') && (
              <EvaluationOverviewList
                title={`${t('general.feedback.culture')} ${t(
                  'evaluationModal.overview'
                )}`}
                icon="Culture"
                entityLabelKey="evaluationModal.value"
                resultsCount={cultureRows.length}
                mode={overviewMode.culture}
                onModeChange={(m) =>
                  setOverviewMode((prev) => ({ ...prev, culture: m }))
                }
                resultsDisabled={!hasAnyResultsFor('culture')}
                rows={cultureRows}
                assessorsContent={renderResults('culture', true)}
                onRowClick={(id) => onOverviewRowClick('culture', id)}
              />
            )}
            {showSection('goals') && (
              <EvaluationOverviewList
                title={
                  t('general.goals.goals') + ' ' + t('evaluationModal.overview')
                }
                icon="Goals"
                entityLabelKey="addNewGoal.goal"
                resultsCount={goalRows.length}
                mode={overviewMode.goals}
                onModeChange={(m) =>
                  setOverviewMode((prev) => ({ ...prev, goals: m }))
                }
                resultsDisabled={!hasAnyResultsFor('goals')}
                rows={goalRows}
                assessorsContent={renderResults('goals', true)}
                onRowClick={(id) => onOverviewRowClick('goals', id)}
              />
            )}
          </Body>
        )
      }
    }
  }

  // An overview entry can spawn a nested results layer; a results entry (opened
  // directly, e.g. from EvaluateRequestModal) renders just itself.
  const isResultsEntry = initialType.includes('Results')

  return company ? (
    <>
      <StickyHeaderLayoverModal
        closeModal={closeModal}
        dropdowns={dropdowns}
        transformDropdown={!type.includes('Results')}
        setDropdowns={setDropdowns}
        headerConfig={{
          subtitle: t(modalDetails.title),
          // Back pops just this layer via the stack (nested instance) or returns
          // to the caller's view (direct entry); the overview base has no back.
          onBack,
          cta:
            type !== 'plannedEvaluation'
              ? {
                  onClick: user?.asCompanyMember?.isAdmin
                    ? () => {
                        history.push(
                          `/company/people/profile/${member._id}/collections`
                        )
                      }
                    : undefined,
                  member: selectedMember ?? member,
                  iconType:
                    type === 'goalsResults'
                      ? 'Goals'
                      : type === 'skillResults'
                      ? 'Skills'
                      : type === 'culturesResults'
                      ? 'Culture'
                      : undefined,
                  text: t(modalDetails.subtitle, {
                    firstName: member.firstName,
                  }),
                }
              : undefined,
          leftContent: (
            <div>
              {type === 'plannedEvaluation' && (
                <Text textStyle="displayS" color="primaryDark" marginTop={4}>
                  {t('evaluationModal.plannedEvaluation')}
                </Text>
              )}
            </div>
          ),
        }}
      >
        <>{returnBody()}</>
      </StickyHeaderLayoverModal>

      {/* Drilling into a result opens it as a nested layer stacked on top of the
          overview, which the stack system shifts left + dims. Back/dim/X are
          handled by the stack (closeModal here only pops this nested layer). */}
      {!isResultsEntry && drill && (
        <EvaluationModal
          member={member}
          evaluations={evaluations}
          evalCompany={eCompany}
          pdp={drill.pdp ?? pdp}
          isOwnProfile={isOwnProfile}
          previousEvaluation={previousEvaluation}
          compareCompanyVersion={compareCompanyVersion}
          addAssessor={addAssessor}
          removeAssessor={removeAssessor}
          reopenAssessment={reopenAssessment}
          defaultFilter={assessmentTypeForResult(drill.resultType)}
          initialType={drill.resultType}
          initialEntityId={drill.entityId}
          initialSelectedMember={drill.selectedMember}
          initialFilteredAssessments={drill.presetAssessments}
          closeModal={() => setDrill(undefined)}
        />
      )}
    </>
  ) : null
}

export default EvaluationModal

const StyledPeopleRow = styled(PeopleRow)`
  margin-bottom: 8px;
`

const Body = styled.div<{ $plain?: boolean }>`
  background-color: ${({ theme, $plain }) =>
    $plain ? 'transparent' : theme.colors.primaryLight};
  border-radius: ${({ $plain }) => ($plain ? '0' : '12px')};
  height: 100%;
  overflow-y: scroll;
  padding: ${({ $plain }) => ($plain ? '0' : '12px 16px')};
  margin-top: 12px;

  ${({ $plain }) =>
    !$plain &&
    mqFrom.M`
    padding: 12px 24px;
  `}

  > * {
    margin-bottom: ${({ $plain }) => ($plain ? '40px' : '24px')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const AddAssessorRow = styled(Container)`
  gap: 16px;
  margin-bottom: 8px;
  cursor: pointer;

  & > :last-child {
    margin-left: auto;
  }
`

const CardBody = styled.div<{ color: AnyColor }>`
  padding: 24px;
  border-radius: 12px;
  background-color: ${({ theme, color }) => theme.colors[color]};
`

const StyledResultsHeader = styled(ResultsHeader)`
  margin-top: 8px;
`
